import { useEffect, useState } from "react";
import Layout2 from "@/components/Layout/Layout2";
import { Switch } from "@/components/ui/switch";
import { getReportSettings, updateReportSettings } from "@/api/reporting";
import useAuth from "@/hooks/useAuth";
import { toast } from "react-hot-toast";

const DEFAULT_SETTINGS = [
  { key: "cash", label: "Cash", x: true, z: true },
  { key: "card", label: "Card", x: true, z: true },
  { key: "pbl", label: "PBL", x: true, z: true },
  { key: "cnp", label: "CNP", x: true, z: true },
  { key: "tips", label: "Tips", x: true, z: true },
  { key: "unpaid", label: "Unpaid", x: true, z: true, dividerTop: true },
  { key: "refund", label: "Refund", x: true, z: true },
  { key: "complimentary", label: "Complimentary", x: true, z: true },
  { key: "compensation", label: "Compensation", x: true, z: true },
  { key: "void", label: "Void", x: true, z: true },
  { key: "plus_adj", label: "Plus Adj", x: true, z: true, dividerTop: true },
  { key: "minus_adj", label: "Minus Adj", x: true, z: true },
];

function ReportSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const updateSetting = (key, field, value) => {
    setSettings((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
    setHasChanges(true);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.restaurant_id) return;

      setLoading(true);
      const response = await getReportSettings(user.restaurant_id);
      setLoading(false);

      if (response?.status !== "success" || !response?.data?.report_setting) {
        toast.error(response?.message || "Failed to load report settings.");
        return;
      }

      const { report_setting } = response.data;
      const xReport = report_setting.x_report || {};
      const zReport = report_setting.z_report || {};

      setSettings(
        DEFAULT_SETTINGS.map((item) => ({
          ...item,
          x: xReport[item.key] ?? item.x,
          z: zReport[item.key] ?? item.z,
        }))
      );
      setHasChanges(false);
    };

    fetchSettings();
  }, [user?.restaurant_id]);

  const saveSettings = async () => {
    if (!user?.restaurant_id) return;

    setSaving(true);
    const payload = {
      x_report: Object.fromEntries(settings.map((item) => [item.key, item.x])),
      z_report: Object.fromEntries(settings.map((item) => [item.key, item.z])),
    };

    const response = await updateReportSettings(user.restaurant_id, payload);
    setSaving(false);

    if (response?.status === "success") {
      setHasChanges(false);
      toast.success("Report settings updated successfully.");
    } else {
      toast.error(response?.message || "Unable to save report settings.");
    }
  };

  return (
    <Layout2
      pageName={{ icon: true, title: "Report Settings" }}
      paths={[
        { name: "Home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Reporting", path: "/reporting" },
        { name: "Report Settings", path: "/reporting/settings" },
      ]}
    >
      <div className="px-4 pb-12">
        <div className="mx-auto w-full max-w-3xl rounded-md border border-cblack6 bg-cblack4 px-6 py-6">
          <p className="text-center text-sm text-cwhite3">
            Configure which data points appear on specific reports by default.
            Adjustments take effect immediately across the system.
          </p>

          <div className="mt-6 overflow-hidden rounded-md border border-cblack6">
            <div className="grid grid-cols-[1.4fr_1fr_1fr] bg-cblack6 px-4 py-3 text-sm font-semibold text-cwhite">
              <span>Data Field</span>
              <span className="text-center">X-Report</span>
              <span className="text-center">Z-Report (End of Day)</span>
            </div>
            {settings.map((row) => (
              <div
                key={row.key}
                className={`grid grid-cols-[1.4fr_1fr_1fr] items-center px-4 py-3 text-sm text-cwhite3 ${
                  row.dividerTop
                    ? "border-t border-dashed border-cblack6"
                    : "border-t border-cblack6"
                }`}
              >
                <span>{row.label}</span>
                <div className="flex justify-center">
                  <Switch
                    checked={row.x}
                    onCheckedChange={(value) =>
                      updateSetting(row.key, "x", value)
                    }
                  />
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={row.z}
                    onCheckedChange={(value) =>
                      updateSetting(row.key, "z", value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={loading || saving || !hasChanges}
            onClick={saveSettings}
            className={`mt-6 rounded-md px-6 py-2 text-sm font-semibold text-cblack bg-color7 ${
              loading || saving || !hasChanges
                ? "cursor-not-allowed opacity-60"
                : "hover:opacity-90"
            }`}
          >
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>
    </Layout2>
  );
}

export default ReportSettings;
